import { useMemo } from 'react';

import type {
  GetSitrepsResponse,
  SitrepViewDataProps,
} from 'components/Reports/reports.types';

type ReportsScope = {
  incidentId: UUID | null;
  aorId: UUID | null;
};

type ScopedSitrepViewData = {
  publishedSitreps: SitrepViewDataProps;
  draftSitreps: SitrepViewDataProps;
  pendingReviews: SitrepViewDataProps;
};

type UseScopedSitrepViewDataArgs = ScopedSitrepViewData & {
  scope: ReportsScope;
};

const filterSitrepsByScope = (
  sitreps: GetSitrepsResponse[],
  { incidentId, aorId }: ReportsScope
) => {
  const sitrepOnly = sitreps.filter((sitrep) => sitrep.is_sitrep);

  if (incidentId) {
    return sitrepOnly.filter((sitrep) => sitrep.incident_id === incidentId);
  }

  if (aorId) {
    return sitrepOnly.filter(
      (sitrep) => sitrep.aor_id === aorId && sitrep.incident_id === null
    );
  }

  return sitrepOnly;
};

const scopeSitrepViewData = (
  sitrepViewData: SitrepViewDataProps,
  scope: ReportsScope
): SitrepViewDataProps => ({
  ...sitrepViewData,
  data: filterSitrepsByScope(sitrepViewData.data, scope),
});

export const useScopedSitrepViewData = ({
  publishedSitreps,
  draftSitreps,
  pendingReviews,
  scope,
}: UseScopedSitrepViewDataArgs): ScopedSitrepViewData => {
  return useMemo(
    () => ({
      publishedSitreps: scopeSitrepViewData(publishedSitreps, scope),
      draftSitreps: scopeSitrepViewData(draftSitreps, scope),
      pendingReviews: scopeSitrepViewData(pendingReviews, scope),
    }),
    [draftSitreps, pendingReviews, publishedSitreps, scope]
  );
};
